import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Card,
  IconButton,
  Modal,
  Paper,
  PaperProps,
  Toolbar,
  Typography,
} from "@mui/material";
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  useCallback,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PostKbn } from "../../../assets/data/Constants";
import TkButton from "../../../components/atoms/TkButton";
import TkDownloadLink from "../../../components/atoms/TkDownloadLink";
import TkModalWithTitle from "../../../components/atoms/TkModalWithTitle";
import TkSelectForm from "../../../components/atoms/TkSelectForm";
import TkTextArea from "../../../components/molecules/TkTextArea";
import { GlobalContext } from "../../../context/globalContext";
import {
  fileToBase64check,
  FileToBase64encode,
} from "../../../utils/TkFileEncode";
import { TkGetCode } from "../../../utils/TkGetCode";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import { httpAPI_ACC003 } from "../api/request";
import "./_style.scss";
import { schema } from "./parts";
import fields from "../../../assets/data/TkFileEncodeProperty";

const DraggableComponent = React.forwardRef<
  HTMLDivElement,
  PaperProps & { children?: React.ReactNode; isOrigin: boolean }
>((props, ref) => {
  const { isOrigin, ...otherProps } = props;
  const [position, setPosition] = useState({
    x: window.innerWidth - 525,
    y: window.innerHeight - 500,
  });
  const [size, setSize] = useState({ width: 525, height: 500 });
  const draggableRef = useRef<HTMLDivElement>(null);
  const resizing = useRef(false);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const resizeOffset = useRef({ width: 0, height: 0 });

  useImperativeHandle(ref, () => draggableRef.current!);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!draggableRef.current) return;

      if (dragging.current) {
        const newX = event.clientX - offset.current.x;
        const newY = event.clientY - offset.current.y;

        const maxX = window.innerWidth - size.width;
        const maxY = isOrigin
          ? window.innerHeight - size.height
          : window.innerHeight - 53;

        const clampedX = Math.max(0, Math.min(newX, maxX));
        const clampedY = Math.max(0, Math.min(newY, maxY));

        setPosition({ x: clampedX, y: clampedY });
      } else if (resizing.current && isOrigin) {
        const minWidth = 400;
        const minHeight = 400;
        const maxWidth = Math.min(1000, window.innerWidth - position.x);
        const maxHeight = Math.min(800, window.innerHeight - position.y);

        const newWidth = Math.max(
          minWidth,
          Math.min(
            maxWidth,
            event.clientX -
              draggableRef.current.getBoundingClientRect().left +
              resizeOffset.current.width
          )
        );
        const newHeight = Math.max(
          minHeight,
          Math.min(
            maxHeight,
            event.clientY -
              draggableRef.current.getBoundingClientRect().top +
              resizeOffset.current.height
          )
        );

        setSize({ width: newWidth, height: newHeight });
      }
    },
    [position, size, isOrigin]
  );

  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - size.width;
      const maxY = isOrigin
        ? window.innerHeight - size.height
        : window.innerHeight - 53;
      setPosition((prevPosition) => ({
        x: Math.min(Math.max(prevPosition.x, 0), maxX),
        y: Math.min(Math.max(prevPosition.y, 0), maxY),
      }));

      const adjustedWidth = Math.min(
        size.width,
        window.innerWidth - position.x - 30
      );
      const adjustedHeight = Math.min(
        size.height,
        window.innerHeight - position.y
      );
      setSize({
        width: Math.max(400, adjustedWidth),
        height: Math.max(400, adjustedHeight),
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMouseMove, size, isOrigin]);

  const handleMouseDown = (event: React.MouseEvent) => {
    if (
      draggableRef.current &&
      event.clientY - draggableRef.current.getBoundingClientRect().top < 48
    ) {
      event.preventDefault();
      dragging.current = true;
      draggableRef.current.style.cursor = "grabbing";
      offset.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
    }
  };

  const handleMouseUp = () => {
    if (dragging.current) {
      dragging.current = false;
      if (draggableRef.current) {
        draggableRef.current.style.cursor = "grab";
      }
    }
    if (resizing.current) {
      resizing.current = false;
      if (draggableRef.current) {
        draggableRef.current.style.cursor = "grab";
      }
    }
  };

  const handleResizeMouseDown = (event: React.MouseEvent) => {
    if (draggableRef.current) {
      event.preventDefault();
      resizing.current = true;
      draggableRef.current.style.cursor = "nwse-resize";
      resizeOffset.current = {
        width:
          size.width -
          (event.clientX - draggableRef.current.getBoundingClientRect().left),
        height:
          size.height -
          (event.clientY - draggableRef.current.getBoundingClientRect().top),
      };
    }
  };

  return (
    <Paper
      ref={draggableRef}
      {...otherProps}
      onMouseDown={handleMouseDown}
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        maxHeight: `800px`,
        maxWidth: `1000px`,
        minHeight: "400px",
        minWidth: "400px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        borderRadius: "8px",
        cursor: "auto",
      }}
    >
      {props.children}
      <Box
        onMouseDown={handleResizeMouseDown}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "20px",
          height: "20px",
          background:
            "linear-gradient(to bottom right, transparent 50%, #59c1bd 50%)",
          cursor: "nwse-resize",
        }}
      />
    </Paper>
  );
});

/**
 * 掲示板用ダイアログ
 * @return MessageDialog
 */
export const MessageDialog = (props: any) => {
  const { open, handleClose, messages, onSend, reStart, onStart } = props;
  const [isOrigin, setIsOrigin] = useState(true);
  const [messageList, setMessageList] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const { handleSubmit, control, reset } = useForm<any>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const [errorMsg, setErrorMsg] = useState("");
  const [disable, setDisable] = useState(false);

  const handleSend = (data: any) => {
    const newMessage = {
      personalType: TkGetCode(PostKbn, "1"),
      message: data.message,
    };
    const newMassages = messageList;
    newMassages.push(newMessage);
    setMessageList([...newMassages]);
    onSend && onSend({ message: data.message });
    setMessage("");
    reset();
  };

  const handleChange = (value: any) => {
    setMessage(value);
    setDisable(value === "");
  };

  const handleFileChange = async (e: any) => {
    const checkResult = fileToBase64check(e.target.files[0]);
    if (
      checkResult !== "" &&
      checkResult !== undefined &&
      checkResult !== null
    ) {
      setErrorMsg(TkPropertyMessageGet(checkResult));
      return;
    }
    setErrorMsg("");

    const encodeResult = await FileToBase64encode(e.target.files[0]);
    const file = {
      fileName: encodeResult?.filename,
      fileBase64: encodeResult?.base64result,
    };
    const newMessage = {
      personalType: TkGetCode(PostKbn, "1"),
      fileName: file.fileName,
      fileBase64: file.fileBase64,
    };
    const newMassages = messageList;
    newMassages.push(newMessage);
    setMessageList([...newMassages]);
    onSend && onSend({ file: file });
  };

  const startClick = () => {
    onStart && onStart();
  };

  const clickRemoveButton = () => {
    setErrorMsg("");
    setIsOrigin(!isOrigin);
  };

  const clickCloseButton = () => {
    setErrorMsg("");
    handleClose && handleClose();
  };

  const messagesEndRef = useRef<HTMLElement>(null);

  // 自動スクロール
  useEffect(() => {
    if (messagesEndRef.current) {
      const scrollHeight: number = messagesEndRef.current.scrollHeight;
      const height: number = messagesEndRef.current.clientHeight;
      const maxScrollTop: number = scrollHeight - height;
      messagesEndRef.current.scrollTop = maxScrollTop;
      messagesEndRef!.current!.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messageList]);

  useEffect(() => {
    setIsOrigin(true);
    setMessageList(open ? messages : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      className={`tkSCRW150002Modal ${!isOrigin ? "modalClose" : ""}`}
      open={open}
      hideBackdrop
      disableScrollLock={true}
      component={DraggableComponent}
      isOrigin={isOrigin}
      aria-labelledby="draggable-modal-title"
    >
      <>
        <AppBar
          position="static"
          id="draggable-modal-title"
          className="tkAppbar"
        >
          <Toolbar variant="dense" className="tkToolbar">
            <Box className="tkTitle">
              <Typography>{"共済契約者との連絡掲示板"}</Typography>
            </Box>
            <Box className="tkHeaderButton">
              <IconButton
                aria-label="cropLandscape"
                size="small"
                onClick={clickRemoveButton}
              >
                {isOrigin ? <RemoveIcon /> : <CropSquareIcon />}
              </IconButton>

              <IconButton
                aria-label="delete"
                size="small"
                onClick={clickCloseButton}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        {isOrigin && (
          <Box className="tkSCRW150002chat">
            {errorMsg && (
              <Box className="alert">
                <Alert severity="error">{errorMsg}</Alert>
              </Box>
            )}
            <Box className="chat-show" ref={messagesEndRef}>
              {messageList.map((i: any, k: any) => (
                <React.Fragment key={k}>
                  <Box
                    className={
                      i.personalType === "機構" ? "tkKIKO" : "tkKEIYAKUSHA"
                    }
                  >
                    {i.personalType === "機構" ? (
                      <Avatar className="tkAvatarKIKO">{"機"}</Avatar>
                    ) : (
                      <Avatar className="tkAvatarKEIYAKUSHA">{"契"}</Avatar>
                    )}

                    <Box
                      className={
                        i.personalType === "機構"
                          ? "tkKIKOMessage"
                          : "tkKEIYAKUSHAMessage"
                      }
                    >
                      {i.message ? (
                        <Card
                          variant="outlined"
                          className={
                            i.personalType === "機構"
                              ? "tkKIKOCard"
                              : "tkKEIYAKUSHACard"
                          }
                        >
                          <Box className="chatDetail">
                            <Typography
                              textAlign="left"
                              sx={{ whiteSpace: "pre-line" }}
                            >
                              {i.message}
                            </Typography>
                          </Box>
                        </Card>
                      ) : i.fileName ? (
                        <Box className="file">
                          <Box className="image" />
                          <TkDownloadLink
                            dataname={i.fileName}
                            base64filedata={i.fileBase64}
                            downloadBase64
                          />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            <Box
              className="form"
              component="form"
              onSubmit={handleSubmit(handleSend)}
            >
              <Controller
                name="message"
                control={control}
                rules={schema}
                render={({ field }) => (
                  <TkTextArea
                    {...field}
                    multiline
                    inputProps={{ maxLength: 85 }}
                    onGetInputValue={(e: any) => handleChange(e)}
                    wantToSetValue={message}
                    disabled={!(reStart || disable)}
                  />
                )}
              />
              <Box className="tkSendButton">
                <label>
                  <AttachFileOutlinedIcon
                    sx={{
                      cursor: `$(${
                        reStart || disable ? "pointer" : "not-allowed"
                      })`,
                    }}
                    color={`${!reStart ? "disabled" : "primary"}`}
                  />
                  <ImageOutlinedIcon
                    sx={{
                      cursor: `$(${
                        reStart || disable ? "pointer" : "not-allowed"
                      })`,
                    }}
                    color={`${!reStart ? "disabled" : "primary"}`}
                  />
                  {(reStart || disable) && (
                    <input
                      type="file"
                      onChange={(e: any) => handleFileChange(e)}
                      style={{ display: "none" }}
                      accept={fields.file.extension}
                      id={`tk-file-upload-input`}
                      data-testid="tkFileUploadInputForTest"
                      disabled={!(reStart || disable)}
                    />
                  )}
                </label>
                <Box>
                  {!reStart ? (
                    <TkButton mr={true} onClick={startClick}>
                      {"開始"}
                    </TkButton>
                  ) : null}
                  <TkButton
                    type="submit"
                    endIcon={<SendIcon />}
                    disabled={!(reStart || disable)}
                  >
                    {"送信"}
                  </TkButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </>
    </Modal>
  );
};

/**
 * 担当者検索ダイアログ
 * @return PersonInChargeSerchDialog
 */

export const PersonInChargeSerchDialog = (props: any) => {
  const { open, contractorNo, onSuccess, onClose } = props;
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const [pullDownList, setPullDownList] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectValue, setSelectValue] = useState("");
  async function openDialog() {
    try {
      const res = await httpAPI_ACC003({ contractorNo: contractorNo });
      if (res.resultCd === "I0000") {
        const personList = res.corpUserList as { [key: string]: any };
        const pullDownData = [];
        for (let i in personList) {
          const data = {
            label:
              (personList[i].kbn === "1" ? "【代行】" : "【担当】") +
              (personList[i].name ?? ""),
            value: personList[i].userId,
          };
          pullDownData.push(data);
        }
        setPullDownList(pullDownData);
        setState({ ...state, errorMsgId: "" });
      } else {
        setState({ ...state, errorMsgId: res.resultCd });
      }
    } catch (error) {
      navigate("/error");
    }
  }

  useEffect(() => {
    open && openDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const clearData = () => {
    setSelectValue("");
    setErrorMsg("");
  };
  const handleOnSave = () => {
    const errorObj = !selectValue ? TkPropertyMessageGet("TKMSG_SCR0044E") : "";
    setErrorMsg(errorObj);
    if (errorObj) {
      return;
    }
    onSuccess && onSuccess(selectValue);

    clearData();
  };
  const handleOnClose = () => {
    clearData();
    onClose && onClose();
  };

  const formValueChange = (e: any) => {
    setSelectValue(e.target.value);
  };
  return (
    <TkModalWithTitle
      open={open}
      title=""
      onSave={handleOnSave}
      onClose={handleOnClose}
      saveButtonName="はい"
      cancelButtonName="いいえ"
    >
      <Box>
        <Typography>
          {"連絡を行う法人の担当者または代行者を選択してください。"}
        </Typography>
        <Box>
          <TkSelectForm
            fullWidth
            error={!!errorMsg}
            errorMessage={errorMsg}
            selectPropsList={pullDownList}
            onChange={formValueChange}
            wantToSetValue={selectValue}
          />
        </Box>
      </Box>
    </TkModalWithTitle>
  );
};
